/**
 * Converts a PDF buffer (array of bytes) to a Blob URL
 * @param buffer - Array of numbers representing PDF bytes or Buffer object
 * @returns Blob URL string that can be used in iframe or download
 */
export const convertBufferToBlobUrl = (
  buffer: number[] | { data: number[] } | Buffer | any,
): string | null => {
  try {
    // Handle different buffer formats
    let bufferArray: number[];

    if (Array.isArray(buffer)) {
      bufferArray = buffer;
    } else if (buffer?.data && Array.isArray(buffer.data)) {
      bufferArray = buffer.data;
    } else if (buffer?.type === "Buffer" && Array.isArray(buffer.data)) {
      bufferArray = buffer.data;
    } else {
      console.error("Invalid buffer format:", buffer);
      return null;
    }

    // Convert array of bytes to Uint8Array
    const uint8Array = new Uint8Array(bufferArray);

    // Create blob from the byte array
    const blob = new Blob([uint8Array], { type: "application/pdf" });

    // Create and return object URL
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error converting buffer to blob URL:", error);
    return null;
  }
};

/**
 * Downloads a PDF from buffer data
 * @param buffer - PDF buffer data
 * @param filename - Name for the downloaded file
 */
export const downloadPdfFromBuffer = (
  buffer: number[] | { data: number[] } | Buffer | any,
  filename: string,
): void => {
  try {
    const blobUrl = convertBufferToBlobUrl(buffer);

    if (!blobUrl) {
      console.error("Failed to create blob URL for download");
      return;
    }

    // Create temporary link and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL after a short delay
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (error) {
    console.error("Error downloading PDF from buffer:", error);
  }
};

/**
 * Checks if data is a buffer format
 */
export const isBufferData = (data: any): boolean => {
  return (
    Array.isArray(data) ||
    (data?.data && Array.isArray(data.data)) ||
    (data?.type === "Buffer" && Array.isArray(data.data))
  );
};

export const viewPDF = (
  buffer: number[] | { data: number[] } | Buffer | any,
  filename: string,
): void => {
  try {
    const url = convertBufferToBlobUrl(buffer);

    if (!url) {
      console.error("Failed to create blob URL from buffer");
      return;
    }

    const newWindow = window.open(url, "_blank");

    if (!newWindow) {
      console.error("Popup blocked. Please allow popups for this site.");
      // Fallback: download if popup is blocked
      downloadPdfFromBuffer(buffer, `${filename}.pdf`);
    } else {
      // Clean up URL after window is loaded
      newWindow.onload = () => {
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  } catch (error) {
    console.error("Error viewing certificate:", error);
  }
};
